import { useMutation } from '@tanstack/react-query';
import { useShowErrorToast } from '@utils/formHelpers';
import { queryFetch } from '@services/api';
import { CopyTaskReq } from './types';
import { useJobRefetch } from '@hooks/job/refetch';
import { DefaultApiResp } from '@root/globalTypes';
import { defaultAppearance } from '@forms/panel/workflows/task/constants';
import { useToast } from '@hooks/useToast';
import { useTranslation } from 'react-i18next';

export const useCopyTask = () => {
  const { refetch, stages } = useJobRefetch();
  const { showErrorToast } = useShowErrorToast();
  const { toast } = useToast();
  const { t } = useTranslation('app');

  const { mutate, isPending } = useMutation({
    mutationFn: ({ jobId, ...task }: CopyTaskReq): Promise<DefaultApiResp> =>
      queryFetch({
        url: `jobs/${jobId}/tasks/copy`,
        data: { task },
        opt: { method: 'POST' },
      }),
    onMutate: async ({ jobId, taskName: name, stageId }) =>
      stages.update({
        jobId,
        stage: {
          id: stageId,
          tasks: stages
            .get(jobId)
            ?.find((el) => el.id === stageId)
            ?.tasks.map((el) => ({
              ...el,
              tasks: [...el.tasks, { id: name, name, appearance: defaultAppearance }],
            })),
        },
      }),
    onError: (err, { jobId }, prev) => {
      console.error(err);
      stages.restore(jobId, prev);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      toast({ title: t('messages.success.task.create') });
      await refetch();
    },
    onSettled: (data) => {
      if (!data) return;
      const { errors } = data as DefaultApiResp;
      if (errors) showErrorToast(errors);
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
